/**
 * WA header — push-down panels (phone menu + inline search).
 * Port of the BU theme's header script (Sloth behaviour), plan §B1 / §F2 "P1".
 *
 *  - every `[data-wa-toggle="<id>"]` button flips the `hidden` attribute on that element and keeps its
 *    own `aria-expanded` in sync;
 *  - when a panel opens, its first real (non-hidden) <input> is focused — that puts the cursor in the
 *    search box;
 *  - the menu and search panels are independent: both may be open at once;
 *  - Escape closes every open panel and, if focus was inside a panel, hands it back to that panel's
 *    button (keyboard users are not dropped at the top of the page);
 *  - the ≡ glyph never changes to an × (Sloth parity).
 *
 * Why one document-level listener instead of one per button: the theme editor re-renders the section
 * when a setting changes, which would drop per-button listeners. A delegated listener survives that.
 *
 * Why no stopPropagation / stopImmediatePropagation anywhere: the pet-art popup on product pages has its
 * own Escape handling, and this file must never swallow an event that code relies on. preventDefault is
 * called only for our own toggle buttons (type="button", so it changes nothing for them either).
 *
 * This file touches only elements carrying `data-wa-toggle` and the panels they name. It reads and
 * writes nothing in the product flow, the cart, or any Wolden / Horizon component.
 */

const TOGGLE = 'data-wa-toggle';

function panelFor(button) {
  const id = button.getAttribute(TOGGLE);
  return id ? document.getElementById(id) : null;
}

function setOpen(button, panel, open) {
  const hadFocusInside = !open && panel.contains(document.activeElement);
  panel.hidden = !open;
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) {
    const input = panel.querySelector('input:not([type="hidden"])');
    if (input) input.focus();
  } else if (hadFocusInside) {
    button.focus();
  }
}

// ES modules run once per document even if the section is re-rendered, but a guard costs nothing and
// protects against the file ever being loaded twice under two URLs.
if (!window.__waHeaderBound) {
  window.__waHeaderBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest(`[${TOGGLE}]`);
    if (!button) return;
    const panel = panelFor(button);
    if (!panel) return;
    event.preventDefault();
    setOpen(button, panel, panel.hidden);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(`[${TOGGLE}]`).forEach((button) => {
      const panel = panelFor(button);
      if (panel && !panel.hidden) setOpen(button, panel, false);
    });
  });
}
